import { useMemo } from 'react';
import { z } from 'zod';
import type { SchemaEntry } from '@/components/validation';
import { validateDomain } from '@/utils/validation/helpers';
import {
  rolesSchema,
  multipliersSchema,
  shortTurnSchema,
  holidaysSchema,
  mealBreakSchema,
  overnightSchema,
  travelSchema,
  invoiceSchema,
  expenseSchema,
} from '@/utils/validation/schemas';
import type { ClientFormData, CustomizedSections } from '../utilities/formDataUtils';
import type { ClientDisplayData } from '@/pages/ClientsPage';

// ---------------------------------------------------------------------------
// Data mapping: ClientFormData (snake_case) → schema-compatible (camelCase)
// ---------------------------------------------------------------------------

const mapClientDataToUserState = (formData: ClientFormData) => ({
  minimumBillableHours: formData.minimum_billable_hours,
  weekStart: formData.week_start,
  halfDayHours: formData.half_day_hours,
  needsTravel:
    formData.per_diem_base_rate != null ||
    formData.travel_day_rate_multiplier != null ||
    !!formData.uses_gsa_rate,
  usesGsaRate: formData.uses_gsa_rate ?? false,
  perDiemBaseRate: formData.per_diem_base_rate,
  travelDayRateMultiplier: formData.travel_day_rate_multiplier,
  paymentTermsDays: formData.payment_terms_days,
  rateMultipliers: formData.rate_multipliers,
  // Premium Shift
  allowPremiumShifts: formData.allow_premium_shifts,
  premiumShiftOffsetMode: formData.premium_shift_offset_mode,
  premiumShiftFixedOffset: formData.premium_shift_fixed_offset,
  premiumShiftMultiplierCode: formData.premium_shift_multiplier_code,
  // Short Turn
  usesShortTurn: formData.uses_short_turn,
  shortTurnConfig: {
    requiredBreakHours: formData.required_break_hours,
    surchargeType: formData.short_turn_surcharge_type,
    shortTurnFixedSurchargeAmount: formData.short_turn_fixed_surcharge_amount,
    overtimeOffset: formData.short_turn_overtime_offset,
    floorTierCode: formData.short_turn_floor_tier_code,
    floorTierCustomValue: formData.short_turn_floor_tier_custom_value,
    surchargeAppliesToShortedHoursOnly: formData.surcharge_applies_to_shorted_hours_only,
  },
  // Holiday Premium
  applyHolidayPremium: formData.apply_holiday_premium,
  holidayCatalogIds: formData.catalog_holiday_ids,
  holidayCustomIds: formData.custom_holiday_ids,
  holidayPremiumType: formData.holiday_premium_type,
  holidayOvertimeOffset: formData.holiday_overtime_offset ?? 1,
  holidayFloorTierCode: formData.holiday_floor_tier_code,
  holidayFloorTierCustomValue: formData.holiday_floor_tier_custom_value,
  holidayFixedPremiumAmount: formData.holiday_fixed_premium_amount,
  surchargeConcurrencyStrategy: formData.surcharge_concurrency_strategy,
  addFixedSurcharges: formData.add_fixed_surcharges,
  // Invoice fields
  useLateFee: formData.use_late_fee,
  lateFeeType: formData.late_fee_type,
  lateFeePercentage: formData.late_fee_percentage,
  lateFeeFixedAmount: formData.late_fee_fixed_amount,
  recurringLateFee: formData.recurring_late_fee,
  recurringLateFeeInterval: formData.recurring_late_fee_interval,
  useEarlyPaymentDiscount: formData.use_early_payment_discount,
  earlyPaymentType: formData.early_payment_type,
  earlyPaymentDiscountPercentage: formData.early_payment_discount_percentage,
  earlyPaymentFixedAmount: formData.early_payment_fixed_amount,
  earlyPaymentDays: formData.early_payment_days,
  useCancellationPolicy: formData.use_cancellation_policy,
  cancellationPolicy: formData.cancellation_policy,
  rushPremiumPolicy: formData.rush_premium_policy,
  autoSendReminders: formData.auto_send_reminders,
  reminderDays: formData.reminder_days,
  employmentType: formData.employment_type,
  mealConfig: {
    applyMealSurcharge: formData.apply_meal_surcharge,
    mealDeadlineHours: formData.meal_deadline_hours,
    recurringMealDeadline: formData.recurring_meal_deadline,
    mealBreakLengthMinutes: formData.meal_break_length_minutes,
    gracePeriodMinutes: formData.grace_period_minutes,
    breakBillingPolicy: formData.break_billing_policy,
    mealSurchargeType: formData.meal_surcharge_type,
    useAdditionalOffset: formData.use_additional_offset,
    creditIntervalMinutes: formData.credit_interval_minutes,
    timeCreditMinutes: formData.time_credit_minutes,
    fixedFeeSchedule: formData.fixed_fee_schedule,
    premiumType: formData.meal_premium_type,
    premiumValue: formData.meal_premium_value,
    premiumBasis: formData.meal_premium_basis,
    floorSource: (formData as Record<string, unknown>).meal_floor_source,
    overtimeOffset: (formData as Record<string, unknown>).meal_overtime_offset,
    floorTierCode: (formData as Record<string, unknown>).meal_floor_tier_code,
    floorTierCustomValue: (formData as Record<string, unknown>).meal_floor_tier_custom_value,
    mealTriggerMode: formData.meal_trigger_mode,
    requiredBreaks: formData.required_breaks,
    thresholdHours: formData.threshold_hours,
    additionalBreakIntervalHours: formData.additional_break_interval_hours,
    requiredBreaksHalfDay: formData.required_breaks_half_day,
  },
  roles: formData.roles,
  overnightConfig: {
    applyOvernightPremium: formData.apply_overnight_premium,
    startTime: formData.overnight_start_time,
    endTime: formData.overnight_end_time,
    overnightMethod: formData.overnight_premium_type,
    premiumType: formData.overnight_hourly_premium_type,
    premiumValue: formData.overnight_hourly_premium_value,
    premiumBasis: formData.overnight_hourly_premium_basis,
    flatRateAmount: formData.overnight_flat_rate_amount,
    timeCreditMinutes: formData.overnight_time_credit_minutes,
    thresholdEnabled: formData.overnight_threshold_enabled,
    thresholdType: formData.overnight_threshold_type,
    percentageThreshold: formData.overnight_percentage_threshold,
    hoursThreshold: formData.overnight_hours_threshold,
    floorSource: (formData as Record<string, unknown>).overnight_floor_source,
    overtimeOffset: (formData as Record<string, unknown>).overnight_overtime_offset,
    floorTierCode: (formData as Record<string, unknown>).overnight_floor_tier_code,
    floorTierCustomValue: (formData as Record<string, unknown>).overnight_floor_tier_custom_value,
  },
  expenseConfig: {
    expensePolicies: formData.expense_policies,
    expenseMileageRate: formData.expense_mileage_rate,
    expenseMileageUnit: formData.expense_mileage_unit,
    defaultExpenseMarkupType: formData.default_expense_markup_type,
    defaultExpenseMarkupPercentage: formData.default_expense_markup_percentage,
    defaultExpenseMarkupFixedAmount: formData.default_expense_markup_fixed_amount,
    expenseReceiptRequiredAboveAmount: formData.expense_receipt_required_above_amount,
  },
});

// ---------------------------------------------------------------------------
// Error field name mapping: camelCase schema paths → snake_case form keys
// ---------------------------------------------------------------------------

const FIELD_NAME_MAP: Record<string, string> = {
  minimumBillableHours: 'minimum_billable_hours',
  weekStart: 'week_start',
  halfDayHours: 'half_day_hours',
  perDiemBaseRate: 'per_diem_base_rate',
  travelDayRateMultiplier: 'travel_day_rate_multiplier',
  paymentTermsDays: 'payment_terms_days',
  rateMultipliers: 'rate_multipliers',
  premiumShiftOffsetMode: 'premium_shift_offset_mode',
  premiumShiftFixedOffset: 'premium_shift_fixed_offset',
  premiumShiftMultiplierCode: 'premium_shift_multiplier_code',
  // Short Turn
  'shortTurnConfig.surchargeType': 'short_turn_surcharge_type',
  'shortTurnConfig.requiredBreakHours': 'required_break_hours',
  'shortTurnConfig.overtimeOffset': 'short_turn_overtime_offset',
  'shortTurnConfig.floorTierCode': 'short_turn_floor_tier_code',
  'shortTurnConfig.shortTurnFixedSurchargeAmount': 'short_turn_fixed_surcharge_amount',
  // Holiday
  applyHolidayPremium: 'apply_holiday_premium',
  holidayPremiumType: 'holiday_premium_type',
  holidayCatalogIds: 'catalog_holiday_ids',
  holidayCustomIds: 'custom_holiday_ids',
  holidayOvertimeOffset: 'holiday_overtime_offset',
  holidayFloorTierCode: 'holiday_floor_tier_code',
  holidayFixedPremiumAmount: 'holiday_fixed_premium_amount',
  holidayHourlyPremiumType: 'holiday_hourly_premium_type',
  holidayHourlyPremiumValue: 'holiday_hourly_premium_value',
  holidayPremiumBasis: 'holiday_premium_basis',
  surchargeConcurrencyStrategy: 'surcharge_concurrency_strategy',
  addFixedSurcharges: 'add_fixed_surcharges',
  // Invoice
  lateFeePercentage: 'late_fee_percentage',
  lateFeeFixedAmount: 'late_fee_fixed_amount',
  recurringLateFeeInterval: 'recurring_late_fee_interval',
  earlyPaymentDiscountPercentage: 'early_payment_discount_percentage',
  earlyPaymentFixedAmount: 'early_payment_fixed_amount',
  earlyPaymentDays: 'early_payment_days',
  cancellationPolicy: 'cancellation_policy',
  'cancellationPolicy.days': 'cancellation_policy',
  'cancellationPolicy.value': 'cancellation_policy',
  reminderDays: 'reminder_days',
  // Meal Break
  'mealConfig.mealDeadlineHours': 'meal_deadline_hours',
  'mealConfig.mealBreakLengthMinutes': 'meal_break_length_minutes',
  'mealConfig.gracePeriodMinutes': 'grace_period_minutes',
  'mealConfig.breakBillingPolicy': 'break_billing_policy',
  'mealConfig.mealSurchargeType': 'meal_surcharge_type',
  'mealConfig.creditIntervalMinutes': 'credit_interval_minutes',
  'mealConfig.timeCreditMinutes': 'time_credit_minutes',
  'mealConfig.fixedFeeSchedule': 'fixed_fee_schedule',
  'mealConfig.premiumType': 'meal_premium_type',
  'mealConfig.premiumValue': 'meal_premium_value',
  'mealConfig.premiumBasis': 'meal_premium_basis',
  'mealConfig.stepUpAfterMinutes': 'step_up_after_minutes',
  'mealConfig.floorSource': 'meal_floor_source',
  'mealConfig.overtimeOffset': 'meal_overtime_offset',
  'mealConfig.floorTierCode': 'meal_floor_tier_code',
  'mealConfig.floorTierCustomValue': 'meal_floor_tier_custom_value',
  // Overnight
  'overnightConfig.startTime': 'overnight_start_time',
  'overnightConfig.endTime': 'overnight_end_time',
  'overnightConfig.overnightMethod': 'overnight_premium_type',
  'overnightConfig.premiumType': 'overnight_hourly_premium_type',
  'overnightConfig.premiumValue': 'overnight_hourly_premium_value',
  'overnightConfig.premiumBasis': 'overnight_hourly_premium_basis',
  'overnightConfig.flatRateAmount': 'overnight_flat_rate_amount',
  'overnightConfig.timeCreditMinutes': 'overnight_time_credit_minutes',
  'overnightConfig.percentageThreshold': 'overnight_percentage_threshold',
  'overnightConfig.hoursThreshold': 'overnight_hours_threshold',
  'overnightConfig.activationLogic': 'overnight_activation_logic',
  'overnightConfig.thresholdType': 'overnight_threshold_type',
  'overnightConfig.floorSource': 'overnight_floor_source',
  'overnightConfig.overtimeOffset': 'overnight_overtime_offset',
  'overnightConfig.floorTierCode': 'overnight_floor_tier_code',
  'overnightConfig.floorTierCustomValue': 'overnight_floor_tier_custom_value',
  // Expense
  'expenseConfig.expenseMileageRate': 'expense_mileage_rate',
  'expenseConfig.defaultExpenseMarkupPercentage': 'default_expense_markup_percentage',
  'expenseConfig.defaultExpenseMarkupFixedAmount': 'default_expense_markup_fixed_amount',
  'expenseConfig.expenseReceiptRequiredAboveAmount': 'expense_receipt_required_above_amount',
  // Expense (without prefix — expenseSchema emits paths without the nested prefix)
  expenseMileageRate: 'expense_mileage_rate',
  defaultExpenseMarkupPercentage: 'default_expense_markup_percentage',
  defaultExpenseMarkupFixedAmount: 'default_expense_markup_fixed_amount',
  expenseReceiptRequiredAboveAmount: 'expense_receipt_required_above_amount',
};

/**
 * Map a Zod issue path (array of string | number segments) to a
 * snake_case field key the client form understands.
 *
 * Zod emits paths like `['shortTurnConfig', 'surchargeType']` or
 * `['cancellationPolicy', 0, 'days']`. We join them with `.` and
 * look up the mapping; for indexed paths like `cancellationPolicy.0.days`
 * we also try the generic `cancellationPolicy.days`.
 */
function mapZodPath(pathSegments: (string | number)[]): string {
  const dotPath = pathSegments.join('.');

  // Direct match
  if (FIELD_NAME_MAP[dotPath]) return FIELD_NAME_MAP[dotPath];

  // Try stripping numeric indices (e.g., "cancellationPolicy.0.days" → "cancellationPolicy.days")
  const withoutIndices = pathSegments.filter((s) => typeof s === 'string').join('.');
  if (FIELD_NAME_MAP[withoutIndices]) return FIELD_NAME_MAP[withoutIndices];

  // Return the dot-path as-is (handles already-mapped fields like 'name', 'domain',
  // and array-indexed paths like 'roles[0].rate' that don't need mapping)
  return dotPath;
}

// ---------------------------------------------------------------------------
// Per-step schema builders
// ---------------------------------------------------------------------------

/**
 * Step 0: Basic Info — name (required + uniqueness) and domain validation.
 */
function buildStep0Schema(
  allClients: ClientDisplayData[],
  isEditing: boolean,
) {
  return z.any().superRefine((formData: ClientFormData, ctx) => {
    // Name required
    if (!formData.name?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['name'],
        message: 'Client name is required',
      });
    } else if (
      allClients.some(
        (client) =>
          client.name?.trim().toLowerCase() === formData.name!.trim().toLowerCase() &&
          (!isEditing || client.id !== formData.id),
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['name'],
        message: 'A client with this name already exists.',
      });
    }

    // Domain format
    if (formData.domain) {
      const domainError = validateDomain(formData.domain);
      if (domainError) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['domain'],
          message: domainError,
        });
      }
    }
  });
}

/**
 * Step 1: Roles — delegates to the rolesSchema.
 */
function buildStep1Schema() {
  return z.any().superRefine((formData: ClientFormData, ctx) => {
    const userState = mapClientDataToUserState(formData);
    const result = rolesSchema.safeParse(userState);
    if (!result.success) {
      for (const issue of result.error.issues) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [mapZodPath(issue.path)],
          message: issue.message,
        });
      }
    }
  });
}

/**
 * Step 2: Multipliers & Surcharges — conditionally validated based on customizedSections.
 */
function buildStep2Schema(customizedSections: CustomizedSections) {
  // Collect applicable domain schemas
  const schemas: z.ZodType[] = [];
  if (customizedSections.multipliers) schemas.push(multipliersSchema);
  if (customizedSections.shortTurn) schemas.push(shortTurnSchema);
  if (customizedSections.holiday) schemas.push(holidaysSchema);
  if (customizedSections.mealBreak) schemas.push(mealBreakSchema);
  if (customizedSections.overnight) schemas.push(overnightSchema);

  return z.any().superRefine((formData: ClientFormData, ctx) => {
    const userState = mapClientDataToUserState(formData);

    for (const schema of schemas) {
      const result = schema.safeParse(userState);
      if (!result.success) {
        for (const issue of result.error.issues) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [mapZodPath(issue.path)],
            message: issue.message,
          });
        }
      }
    }
  });
}

/**
 * Step 3: Billing & Invoicing — conditionally includes travel, invoice, expense schemas.
 */
function buildStep3Schema(
  customizedSections: CustomizedSections,
  isTravelManuallyEnabled: boolean,
) {
  const schemas: z.ZodType[] = [];
  if (customizedSections.billing) schemas.push(travelSchema);
  if (customizedSections.invoicing) schemas.push(invoiceSchema);
  if (customizedSections.expenses) schemas.push(expenseSchema);

  return z.any().superRefine((formData: ClientFormData, ctx) => {
    const userState = mapClientDataToUserState(formData);

    for (const schema of schemas) {
      let dataForSchema = userState;

      // Travel schema: merge the manual toggle into needsTravel
      if (schema === travelSchema) {
        dataForSchema = {
          ...userState,
          needsTravel: isTravelManuallyEnabled || userState.needsTravel,
        };
      }

      const result = schema.safeParse(dataForSchema);
      if (!result.success) {
        for (const issue of result.error.issues) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [mapZodPath(issue.path)],
            message: issue.message,
          });
        }
      }
    }
  });
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseClientSchemaMapProps {
  formData: ClientFormData;
  customizedSections: CustomizedSections;
  isTravelManuallyEnabled: boolean;
  allClients: ClientDisplayData[];
  isEditing: boolean;
}

/**
 * Builds a stable `schemaMap` keyed by step number (as string) for the
 * client wizard's `ValidationProvider`.
 *
 * Steps 0-3 each get a Zod schema that validates against the domain
 * schemas from `@/utils/validation/schemas`.
 * Step 4 (Review) has no validation of its own.
 */
export function useClientSchemaMap({
  formData,
  customizedSections,
  isTravelManuallyEnabled,
  allClients,
  isEditing,
}: UseClientSchemaMapProps): Record<string, SchemaEntry> {
  return useMemo(() => ({
    '0': {
      schema: buildStep0Schema(allClients, isEditing),
      data: formData,
    },
    '1': {
      schema: buildStep1Schema(),
      data: formData,
    },
    '2': {
      schema: buildStep2Schema(customizedSections),
      data: formData,
    },
    '3': {
      schema: buildStep3Schema(customizedSections, isTravelManuallyEnabled),
      data: formData,
    },
  }), [formData, customizedSections, isTravelManuallyEnabled, allClients, isEditing]);
}
